import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * GlobalErrorBoundary — catches unhandled React render errors across all
 * role surfaces and displays a branded recovery screen instead of a blank page.
 *
 * Placed at the top level of App.tsx, wrapping all routes and providers
 * (except QueryClientProvider which must remain outside for cache persistence).
 *
 * Users can recover by refreshing the page. The error is logged to console
 * (and will be captured by Datadog RUM / Sentry when configured).
 */
export class GlobalErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[GlobalErrorBoundary] Uncaught render error:', error, errorInfo);
  }

  private handleRefresh = (): void => {
    window.location.reload();
  };

  private handleGoHome = (): void => {
    window.location.href = '/';
  };

  override render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--bg-page)] px-6 text-center">
          <div className="mb-6 flex size-16 items-center justify-center rounded-full bg-[var(--status-amber-tint)]">
            <svg
              className="size-8 text-[var(--status-amber-text)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
          </div>

          <h1 className="mb-2 font-[family-name:var(--font-display)] text-2xl text-[var(--text-primary)]">
            Something went wrong
          </h1>

          <p className="mb-8 max-w-md text-sm text-[var(--text-secondary)]">
            An unexpected error occurred. Your data is safe — please refresh the page to continue.
            If the problem persists, contact support.
          </p>

          <div className="flex gap-3">
            <button
              onClick={this.handleGoHome}
              className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] px-5 py-2.5 text-sm font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-surface-raised)] focus:outline-2 focus:outline-offset-2 focus:outline-[var(--border-focus)]"
            >
              Go to Home
            </button>
            <button
              onClick={this.handleRefresh}
              className="rounded-lg bg-[var(--cta-primary-bg)] px-5 py-2.5 text-sm font-medium text-[var(--cta-primary-text)] transition-colors hover:opacity-90 focus:outline-2 focus:outline-offset-2 focus:outline-[var(--border-focus)]"
            >
              Refresh Page
            </button>
          </div>

          {import.meta.env.DEV && this.state.error && (
            <details className="mt-8 w-full max-w-lg text-left">
              <summary className="cursor-pointer text-xs text-[var(--text-tertiary)]">
                Error details (dev only)
              </summary>
              <pre className="mt-2 overflow-auto rounded-md border border-[var(--border-default)] bg-[var(--bg-surface)] p-3 text-xs text-[var(--status-red-text)]">
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
